import { useState, useEffect, useMemo, useRef } from "react"
import { createPortal } from "react-dom"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useOrganization } from "@/contexts/organization-context"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { SmartReportInput, validateConditional, normalizeConditional } from "@/components/ui/smart-report-input"
import { Textarea } from "@/components/ui/textarea"
import { MultiSelect, type MultiSelectOption } from "@/components/ui/multi-select"
import { OrderedMultiSelect } from "@/components/ui/ordered-multi-select"
import { SingleSelect } from "@/components/ui/single-select"
import { SmartDateInput } from "@/components/ui/smart-date-picker"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { toast } from "sonner"
import { api } from "@/services/api"
import { integrationsService, type IntegrationType } from "@/services/integrations"
import { agentsService, type Agent } from "@/services/agents"
import {
  Save,
  Download,
  Share2,
  FileText,
  Loader2,
  Trash2,
  HelpCircle,
  Maximize2,
  Minimize2,
  Play,
  FolderOpen,
  Search,
  Check,
  MoreVertical,
  Sparkles,
  SlidersHorizontal,
  Send,
  Clock,
  Plus,
  Settings,
  ArrowUp,
  ArrowDown
} from "lucide-react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu"
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuCheckboxItem,
} from "@/components/ui/context-menu"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

interface PrimitiveData {
  id?: number
  name: string
  slug?: string
  description?: string
  type?: string
  aggregation?: string
  formula?: string
}

interface SavedReport {
  id: number
  name: string
  description?: string
  configuration: any
  created_at: string
  updated_at: string
}

interface ReportData {
  [key: string]: any
}

interface PivotConfig {
  aggregatorName: string
  rows: string[]
  cols: string[]
  vals: string[]
}

// Available aggregation functions

// Helper to convert report name to URL-friendly slug
const toReportSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/×/g, '_')  // Handle legacy report names with × separator
    .replace(/[^a-z0-9_-]/g, '')
    .replace(/_+/g, '_')  // Collapse multiple underscores
}

// Helper to match a URL slug to a report name (case-insensitive, slug-normalized)
const findReportBySlug = (reports: SavedReport[], slug: string): SavedReport | undefined => {
  return reports.find(r => toReportSlug(r.name) === slug.toLowerCase())
}

export function ReportsPage() {
  const { currentOrganization } = useOrganization()
  const navigate = useNavigate()
  const { orgSlug } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Data state
  const [data, setData] = useState<ReportData[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingData, setLoadingData] = useState(false)
  const [hasRunReport, setHasRunReport] = useState(false)
  
  // Pivot configuration
  const [pivotConfig, setPivotConfig] = useState<PivotConfig>({
    aggregatorName: 'Sum',
    rows: [],
    cols: [],
    vals: []
  })
  
  // Filter state
  const [showTotals, setShowTotals] = useState(true)
  const [showNumFormatted, setShowNumFormatted] = useState(true)
  const [hideEmptyColumns, setHideEmptyColumns] = useState(true)
  const [freezeDimensions, setFreezeDimensions] = useState(true)
  const [autoLoadReports, setAutoLoadReports] = useState(() => {
    const saved = localStorage.getItem('glaium-reports-autoload')
    return saved !== 'false' // Default to true
  })
  const [availableMetrics, setAvailableMetrics] = useState<PrimitiveData[]>([])
  const [availableDimensions, setAvailableDimensions] = useState<PrimitiveData[]>([])
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([])
  const [selectedDimensions, setSelectedDimensions] = useState<string[]>([])
  const [selectedPivot, setSelectedPivot] = useState<string[]>([])
  const [conditionals, setConditionals] = useState<string>('')
  const [conditionalError, setConditionalError] = useState<string | null>(null)

  // Push integrations state
  const [pushIntegrations, setPushIntegrations] = useState<IntegrationType[]>([])
  const [selectedPushIntegration, setSelectedPushIntegration] = useState<string>('none')
  const [selectedFrequency, setSelectedFrequency] = useState<string>('once')
  
  // Date range state (unified for both relative and absolute dates)
  const [dateRange, setDateRange] = useState<{from?: Date | string, to?: Date | string}>({})
  
  // Saved reports
  const [savedReports, setSavedReports] = useState<SavedReport[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [selectedReport, setSelectedReport] = useState<SavedReport | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [reportName, setReportName] = useState('')
  const [reportDescription, setReportDescription] = useState('')
  const [reportSearchQuery, setReportSearchQuery] = useState('')
  
  // Track the current data export ID for updating instead of duplicating
  const [currentExportId, setCurrentExportId] = useState<number | null>(null)

  // Memoize the selected report ID to avoid closure issues in the dialog
  const selectedReportId = useMemo(() => selectedReport?.id ?? null, [selectedReport?.id])
  
  // Track the CSV URL from the optimizer for direct export
  const [csvUrl, setCsvUrl] = useState<string | null>(null)

  // First-time visitor hint for Load Reports button
  const [showLoadReportsHint, setShowLoadReportsHint] = useState(false)
  
  // Track the filter configuration that was actually applied to get the current data
  const [appliedMetrics, setAppliedMetrics] = useState<string[]>([])
  const [appliedDimensions, setAppliedDimensions] = useState<string[]>([])
  const [appliedPivot, setAppliedPivot] = useState<string[]>([])
  
  // Per-column total aggregation overrides (e.g. { "dau": "avg" })
  // Persisted in report.configuration.column_aggregations
  const [columnAggregations, setColumnAggregations] = useState<Record<string, string>>({})

  // Track maximize state for the data table
  const [isMaximized, setIsMaximized] = useState(false)

  // Sort state for table columns
  const [sortConfig, setSortConfig] = useState<{ column: string | null, direction: 'asc' | 'desc' | null }>({ column: null, direction: null })

  // Smart vs Advanced mode toggle
  const [isQuickMode, setIsQuickMode] = useState(true)

  // Track if the report configuration has unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const isLoadingReportRef = useRef(false)
  const isLoadingDataRef = useRef(false)
  const originalReportRef = useRef<{ name: string; description: string } | null>(null)

  // Trigger for auto-loading data after report configuration is loaded
  const [autoLoadTrigger, setAutoLoadTrigger] = useState(0)

  // Auto-load data when report is loaded (after state updates are complete)
  useEffect(() => {
    if (autoLoadTrigger > 0 && selectedMetrics.length > 0 && autoLoadReports) {
      if (isLoadingDataRef.current) return
      console.log('🚀 Auto-applying filters for loaded report (triggered by state update)')
      loadData()
    }
  }, [autoLoadTrigger])

  // Handle ESC key to exit fullscreen
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMaximized) {
        setIsMaximized(false)
      }
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [isMaximized])

  // Detect configuration changes when a report is selected
  useEffect(() => {
    if (!selectedReport || !selectedReport.configuration || isLoadingReportRef.current || !originalReportRef.current) {
      setHasUnsavedChanges(false)
      return
    }

    // Compare name and description
    const currentName = selectedReport.name || ''
    const savedName = originalReportRef.current.name || ''
    const currentDescription = selectedReport.description || ''
    const savedDescription = originalReportRef.current.description || ''

    // Compare current configuration with saved report (order matters for drag-and-drop)
    const savedConfig = selectedReport.configuration
    const currentMetrics = JSON.stringify(selectedMetrics)
    const savedMetrics = JSON.stringify(savedConfig.metrics || [])
    const currentDimensions = JSON.stringify(selectedDimensions)
    const savedDimensions = JSON.stringify(savedConfig.dimensions || [])
    const currentPivot = JSON.stringify(selectedPivot)
    const savedPivot = JSON.stringify(savedConfig.pivot || [])
    const currentConditionals = conditionals.trim()
    const savedConditionals = (savedConfig.conditionals || savedConfig.conditional || '').trim()

    // Convert current dateRange object to period array format for comparison
    const formatDate = (date: Date | string): string => {
      if (typeof date === 'string') return date
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    const currentPeriod = (dateRange?.from || dateRange?.to)
      ? [
          dateRange.from ? formatDate(dateRange.from) : '',
          dateRange.to ? formatDate(dateRange.to) : ''
        ].filter(Boolean)
      : []
    const savedPeriod = savedConfig.period || []

    const hasChanges =
      currentName !== savedName ||
      currentDescription !== savedDescription ||
      currentMetrics !== savedMetrics ||
      currentDimensions !== savedDimensions ||
      currentPivot !== savedPivot ||
      currentConditionals !== savedConditionals ||
      JSON.stringify(currentPeriod) !== JSON.stringify(savedPeriod)

    setHasUnsavedChanges(hasChanges)
  }, [selectedReport, selectedMetrics, selectedDimensions, selectedPivot, conditionals, dateRange])

  // Check if user is visiting Reports page for the first time
  useEffect(() => {
    const hasVisitedReports = localStorage.getItem('glaium-reports-visited')
    if (!hasVisitedReports) {
      setShowLoadReportsHint(true)
    }
  }, [])

  // Helper function to convert date to appropriate format for API
  const formatDateForAPI = (date: Date | string): string => {
    // If it's already a relative date format string (user-typed), return as-is
    if (typeof date === 'string' && /^d-?\d+$/.test(date)) {
      return date
    }
    
    // If it's already a string in YYYY-MM-DD format, return as-is
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date
    }
    
    // Only convert Date objects to string format using local date to preserve user intent
    if (date instanceof Date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    
    // Fallback for other string formats
    return date.toString()
  }

  // Helper function to parse date from saved report format - no timezone conversions
  const parseDateFromReport = (dateStr: string): Date | string => {
    // Handle relative date formats (d0, d-1, d-20, etc.) - return as string
    if (/^d-?\d+$/.test(dateStr)) {
      return dateStr
    }
    
    // If it's in YYYY-MM-DD format, create Date object using local time to preserve the date
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number)
      // Create Date in local time (month is 0-indexed) - this preserves the exact date
      return new Date(year, month - 1, day)
    }
    
    // For other formats, create Date object normally
    return new Date(dateStr)
  }

  useEffect(() => {
    console.log('🔧 useEffect triggered, currentOrganization:', currentOrganization)
    console.log('🔧 currentOrganization?.id:', currentOrganization?.id)

    if (currentOrganization) {
      console.log('✅ currentOrganization exists, loading data...')
      loadPrimitives()
      loadSavedReports()
      loadPushIntegrations()
      loadAgentsForUsage()
    } else {
      console.log('❌ No currentOrganization, skipping data load')
    }
  }, [currentOrganization?.id])

  // Auto-load report from URL parameter when reports AND primitives are loaded
  // Supports both ?report=name format and ?name format (key without value)
  useEffect(() => {
    // Wait for both savedReports AND availableMetrics to be loaded
    if (savedReports.length === 0 || availableMetrics.length === 0 || selectedReport) return

    // First check for ?report=slug format
    let reportSlug = searchParams.get('report')

    // If no report param, check for report name as a key without value (e.g., ?last_30_days)
    if (!reportSlug) {
      for (const [key, value] of searchParams.entries()) {
        // Look for a key that has no value (empty string) and matches a report
        if (value === '' && findReportBySlug(savedReports, key)) {
          reportSlug = key
          break
        }
      }
    }

    if (reportSlug) {
      const report = findReportBySlug(savedReports, reportSlug)
      if (report) {
        console.log('📂 Auto-loading report from URL:', report.name)
        handleLoadReport(report, false) // Don't update URL since we're loading from it
      } else {
        console.warn('⚠️ Report not found for URL slug:', reportSlug)
      }
    }
  }, [savedReports, availableMetrics, searchParams])

  const loadPrimitives = async () => {
    try {
      const response = await api.get('/data/primitives?fields=full')
      const primitives = response.data.primitives || []
      
      // Separate metrics and dimensions based on type
      const metrics = primitives.filter((p: PrimitiveData) => 
        p.type === 'metric' || p.type === 'calculation'
      )
      const dimensions = primitives.filter((p: PrimitiveData) => 
        p.type === 'dimension' || p.type === 'attribute'
      )
      
      setAvailableMetrics(metrics)
      setAvailableDimensions(dimensions)
      setConditionalError(null)
    } catch (error) {
      console.error('Error loading primitives:', error)
      toast.error('Failed to load available metrics and dimensions')
    } finally {
      setLoading(false)
    }
  }
  
  const loadData = async () => {
    if (!currentOrganization || selectedMetrics.length === 0) {
      return
    }

    // Validate conditionals before making API call
    if (conditionals.trim()) {
      const metricOpts = availableMetrics.map(m => ({ label: m.name, value: m.name }))
      const dimOpts = availableDimensions.map(d => ({ label: d.name, value: d.name }))
      const validation = validateConditional(conditionals.trim(), metricOpts, dimOpts)
      if (!validation.valid) {
        setConditionalError(validation.error || 'Invalid conditional')
        toast.error("Invalid Conditional", {
          description: validation.error || "Please check your conditional syntax.",
          duration: 5000,
        })
        return
      }
      setConditionalError(null)
    }

    // Validate date range before making API call
    if (dateRange?.from && dateRange?.to) {
      // Helper function to parse both relative and absolute dates
      const parseDate = (value: Date | string): Date | null => {
        if (value instanceof Date) {
          return value
        }
        
        // Check if it's a relative date format (d0, d-1, d-20, etc.)
        if (typeof value === 'string' && /^d-?\d+$/.test(value)) {
          const today = new Date()
          if (value === 'd0') {
            return today
          } else if (value.startsWith('d-')) {
            const daysAgo = parseInt(value.substring(2))
            const date = new Date(today)
            date.setDate(today.getDate() - daysAgo)
            return date
          }
        }
        
        // Try parsing as absolute date
        const parsed = new Date(value)
        return isNaN(parsed.getTime()) ? null : parsed
      }
      
      const startDate = parseDate(dateRange.from)
      const endDate = parseDate(dateRange.to)
      
      // Check if both dates are valid and start <= end
      if (startDate && endDate && startDate > endDate) {
        toast.error("Invalid Date Range", {
          description: "Start date cannot be after end date. Please check your date selection.",
          duration: 5000,
          style: {
            background: 'rgb(254 242 242)',
            color: 'rgb(127 29 29)',
            border: '1px solid rgb(239 68 68)',
          }
        })
        return
      }
    }
    
    isLoadingDataRef.current = true
    setLoadingData(true)
    // Clear previous CSV URL since we're loading new data
    setCsvUrl(null)
    setCurrentExportId(null)
    try {
      // Build period array only if dates are specified
      const period: string[] | undefined = (dateRange?.from || dateRange?.to)
        ? [
            dateRange?.from ? formatDateForAPI(dateRange.from) : formatDateForAPI(dateRange.to!),
            dateRange?.to ? formatDateForAPI(dateRange.to) : formatDateForAPI(dateRange.from!)
          ]
        : undefined

      // Include pivot fields as regular dimensions so we get the data
      const allDimensions = [...selectedDimensions, ...selectedPivot]

      const requestPayload = {
        organization_id: currentOrganization.id,
        output: 'csv', // Use CSV to avoid nginx timeout - returns URL quickly
        metrics: selectedMetrics,
        dimensions: allDimensions, // Include pivot fields as dimensions
        timeout: 300, // Allow up to 5 minutes for complex queries
        force_refresh: true,
        cache_buster: Date.now(), // Add timestamp to ensure fresh data
        ...(period && { period }),
        ...(conditionals.trim() && { conditional: conditionals.trim() }),
        ...(selectedFrequency !== 'once' && { frequency: selectedFrequency }),
        ...(selectedPushIntegration !== 'none' && { push: [parseInt(selectedPushIntegration)] })
      }
      
      // Debug logging for conditional filtering
      console.log('🔍 Report Request Payload:', {
        metrics: requestPayload.metrics,
        dimensions: requestPayload.dimensions,
        period: requestPayload.period,
        conditional: requestPayload.conditional || 'none',
        push: requestPayload.push || 'none',
        frequency: requestPayload.frequency || 'once',
        timeout: requestPayload.timeout,
        cache_buster: requestPayload.cache_buster,
        organization_id: requestPayload.organization_id
      })
      
      if (conditionals.trim()) {
        console.log('✅ Conditional filter applied:', conditionals.trim())
      } else {
        console.log('ℹ️ No conditional filter applied')
      }
      
      const response = await api.post('/data/retrieve', requestPayload)

      // Log response details
      console.log('📊 Report Response:', {
        export_id: response.data.export_id,
        csv_url: response.data.csv_url,
        data_length: response.data.data?.length || 0,
        file_url: response.data.file_url,
        status: response.data.status,
        message: response.data.message
      })

      // Check for error status in the response (API returns 200 but with error data)
      if (response.data.status === 'error') {
        const errorMessage = response.data.message || 'An error occurred while generating the report'
        console.error('❌ Report Error:', errorMessage)
        toast.error('Report Error', {
          description: errorMessage,
          duration: Infinity, // Permanent toast - user must dismiss
          action: {
            label: 'Dismiss',
            onClick: () => {}
          }
        })
        setData([])
        setHasRunReport(true)
        return
      }

      // Store the export ID for potential update when user saves the report
      if (response.data.export_id) {
        setCurrentExportId(response.data.export_id)
      }

      // Store the CSV URL for direct export (avoids regenerating CSV)
      const csvFileUrl = response.data.csv_url || response.data.file_url
      if (csvFileUrl) {
        setCsvUrl(csvFileUrl)
        console.log('💾 CSV URL received:', csvFileUrl)

        // Fetch and parse CSV data for the UI
        console.log('📥 Fetching CSV data...')
        const csvResponse = await fetch(csvFileUrl)
        const csvText = await csvResponse.text()

        // Parse CSV to JSON
        const lines = csvText.trim().split('\n')
        const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''))
        const parsedData = lines.slice(1).map(line => {
          // Handle quoted values with commas inside
          const values: string[] = []
          let current = ''
          let inQuotes = false
          for (const char of line) {
            if (char === '"') {
              inQuotes = !inQuotes
            } else if (char === ',' && !inQuotes) {
              values.push(current.trim().replace(/^"|"$/g, ''))
              current = ''
            } else {
              current += char
            }
          }
          values.push(current.trim().replace(/^"|"$/g, ''))

          const row: Record<string, any> = {}
          headers.forEach((header, i) => {
            const value = values[i] || ''
            // Try to parse as number only if the entire string is numeric
            // This prevents dates like "2026-01-21" from being parsed as 2026
            const isNumeric = value !== '' && !isNaN(Number(value)) && isFinite(Number(value))
            row[header] = isNumeric ? Number(value) : value
          })
          return row
        })

        console.log(`📊 Parsed ${parsedData.length} rows from CSV`)

        // Log data sample for conditional verification
        if (parsedData.length > 0 && conditionals.trim()) {
          console.log('🔎 Sample of filtered data (first 5 rows):', parsedData.slice(0, 5))

          // If filtering by a numeric field, show min/max values
          const conditionalField = conditionals.match(/(\w+)\s*[><=]/)?.[1]
          if (conditionalField && parsedData[0][conditionalField] !== undefined) {
            const values = parsedData.map((row: any) => parseFloat(row[conditionalField]) || 0)
            console.log(`📈 ${conditionalField} range: min=${Math.min(...values)}, max=${Math.max(...values)}`)
          }
        }

        setData(parsedData)
        setHasRunReport(true)
      } else {
        // Fallback to direct data if no CSV URL (shouldn't happen with output: 'csv')
        const transformedData = response.data.data || []

        // Log data sample for conditional verification
        if (transformedData.length > 0 && conditionals.trim()) {
          console.log('🔎 Sample of filtered data (first 5 rows):', transformedData.slice(0, 5))

          // If filtering by a numeric field, show min/max values
          const conditionalField = conditionals.match(/(\w+)\s*[><=]/)?.[1]
          if (conditionalField && transformedData[0][conditionalField] !== undefined) {
            const values = transformedData.map((row: any) => parseFloat(row[conditionalField]) || 0)
            console.log(`📈 ${conditionalField} range: min=${Math.min(...values)}, max=${Math.max(...values)}`)
          }
        }

        setData(transformedData)
        setHasRunReport(true)
      }
      
      // Store the filter configuration that was actually applied to get this data
      setAppliedMetrics([...selectedMetrics])
      setAppliedDimensions([...selectedDimensions])
      setAppliedPivot([...selectedPivot])
      
      // Update pivot configuration
      setPivotConfig({
        aggregatorName: 'Sum',
        rows: selectedDimensions,
        cols: selectedPivot,
        vals: selectedMetrics.length > 0 ? [selectedMetrics[0]] : []
      })
      
    } catch (error: any) {
      console.error('Error loading data:', error)
      console.error('Error response:', error.response?.data)

      // Extract error message from API response
      const errorMessage = error.response?.data?.detail
        || error.response?.data?.message
        || error.message
        || 'Failed to load report data'

      toast.error('Report Error', {
        description: errorMessage,
        duration: Infinity, // Permanent toast - user must dismiss
        action: {
          label: 'Dismiss',
          onClick: () => {}
        }
      })
    } finally {
      setLoadingData(false)
      isLoadingDataRef.current = false
    }
  }

  const loadSavedReports = async () => {
    try {
      const response = await api.get('/data/reports')
      const reports = response.data.exports || []
      console.log('📚 Loaded saved reports:', reports.length)
      if (reports.length > 0) {
        console.log('📋 Sample report configuration:', reports[0].configuration)
      }
      // Sort reports alphabetically by name
      const sortedReports = reports.sort((a: SavedReport, b: SavedReport) => 
        a.name.localeCompare(b.name)
      )
      setSavedReports(sortedReports)
    } catch (error) {
      console.error('Error loading saved reports:', error)
      // Don't show error as this is not critical
    }
  }

  // Load agents so we can warn when deleting a report bound to one or more agents
  const loadAgentsForUsage = async () => {
    if (!currentOrganization) return
    try {
      const fetched = await agentsService.getAgents(currentOrganization.id)
      setAgents(fetched)
    } catch (error) {
      console.error('Error loading agents for report-usage check:', error)
      setAgents([])
    }
  }

  // Count agents whose bound dataset is the given report
  const agentsUsingReport = (reportId: number | undefined): Agent[] => {
    if (reportId == null) return []
    return agents.filter(a => a.dataset_report_id === reportId)
  }

  const loadPushIntegrations = async () => {
    console.log('🔄 loadPushIntegrations called, currentOrganization:', currentOrganization?.id)
    
    if (!currentOrganization) {
      console.log('❌ No current organization, returning')
      return
    }

    try {
      console.log('📡 Calling getPushIntegrations API...')
      const pushIntegrationsResponse = await integrationsService.getPushIntegrations()
      console.log('✅ getPushIntegrations response:', pushIntegrationsResponse)
      
      const pushIntegrationIds = pushIntegrationsResponse.push_integrations || []
      console.log('📋 Push integration IDs:', pushIntegrationIds)
      
      // Convert the ID-only response to full integration objects by fetching details
      const pushIntegrations: IntegrationType[] = []
      for (const pushInt of pushIntegrationIds) {
        try {
          console.log(`🔍 Loading details for push integration ${pushInt.id}...`)
          const integration = await integrationsService.show(pushInt.id)
          pushIntegrations.push(integration)
          console.log(`✅ Loaded integration ${pushInt.id}: ${integration.name}`)
        } catch (error) {
          console.warn(`Failed to load details for push integration ${pushInt.id}:`, error)
        }
      }
      
      console.log('🎯 Final push integrations:', pushIntegrations)
      console.log('📋 Push integrations with IDs:', pushIntegrations.map(pi => ({ id: pi.id, name: pi.name })))
      setPushIntegrations(pushIntegrations)
    } catch (error) {
      console.error('❌ Error loading push integrations:', error)
      console.error('Error details:', (error as any).response?.data)
      console.error('Error status:', (error as any).response?.status)
      console.error('Error validation details:', (error as any).response?.data?.details)
      // Set empty array on error so UI still works
      setPushIntegrations([])
    }
  }

  const handleConditionalHelp = () => {
    navigate(`/${orgSlug}/help#conditionals`)
  }

  const handleSaveReport = async () => {
    // For existing reports, use the inline-edited name; for new reports, use the dialog input or auto-generated title
    const nameToSave = selectedReport ? selectedReport.name : (reportName.trim() || getReportTitle())
    if (!nameToSave.trim()) {
      toast.error('Please enter a report name')
      return
    }
    
    try {
      // Build period array only if dates are specified
      const period: string[] | undefined = (dateRange?.from || dateRange?.to)
        ? [
            dateRange?.from ? formatDateForAPI(dateRange.from) : formatDateForAPI(dateRange.to!),
            dateRange?.to ? formatDateForAPI(dateRange.to) : formatDateForAPI(dateRange.from!)
          ]
        : undefined

      // Clean configuration by removing empty/default values
      const cleanConfiguration = (config: any) => {
        const cleaned: any = {}

        // Always include these fields if they have values
        if (config.aggregatorName) cleaned.aggregatorName = config.aggregatorName
        if (config.rows && config.rows.length > 0) cleaned.rows = config.rows
        if (config.cols && config.cols.length > 0) cleaned.cols = config.cols
        if (config.vals && config.vals.length > 0) cleaned.vals = config.vals
        if (config.metrics && config.metrics.length > 0) cleaned.metrics = config.metrics
        if (config.dimensions && config.dimensions.length > 0) cleaned.dimensions = config.dimensions
        if (config.pivot && config.pivot.length > 0) cleaned.pivot = config.pivot
        if (config.period && config.period.length > 0) cleaned.period = config.period

        // Only include conditional if it has a non-empty value
        if (config.conditional && config.conditional.trim() !== '') {
          cleaned.conditional = config.conditional
        }

        // Only include push integration if it has a valid value (not 'none')
        if (config.pushIntegration && config.pushIntegration !== 'none') {
          cleaned.pushIntegration = config.pushIntegration
        }

        // Only include frequency if it's not the default 'once'
        if (config.frequency && config.frequency !== 'once') {
          cleaned.frequency = config.frequency
        }

        // Persist per-column total aggregation overrides
        if (config.column_aggregations && Object.keys(config.column_aggregations).length > 0) {
          cleaned.column_aggregations = config.column_aggregations
        }

        return cleaned
      }

      const rawConfiguration = {
        aggregatorName: pivotConfig.aggregatorName,
        rows: pivotConfig.rows,
        cols: pivotConfig.cols,
        vals: pivotConfig.vals,
        metrics: selectedMetrics,
        dimensions: selectedDimensions,
        pivot: selectedPivot,
        period: period,
        conditional: conditionals,
        pushIntegration: selectedPushIntegration,  // Keep for frontend compatibility
        push: selectedPushIntegration !== 'none' ? [parseInt(selectedPushIntegration)] : [],  // Backend expects array
        frequency: selectedFrequency,
        column_aggregations: columnAggregations
      }
      
      const configuration = cleanConfiguration(rawConfiguration)
      
      const savePayload = {
        name: nameToSave,
        description: selectedReport?.description || reportDescription,
        configuration
      }
      
      console.log('📄 Configuration object:', JSON.stringify(configuration, null, 2))
      
      // If we have a selected report or current export ID, update it instead of creating new one
      const reportIdToUpdate = selectedReport?.id || currentExportId
      let savedReport: SavedReport
      if (reportIdToUpdate) {
        const response = await api.put(`/data/reports/${reportIdToUpdate}`, savePayload)
        savedReport = response.data
      } else {
        const response = await api.post('/data/reports', savePayload)
        savedReport = response.data
      }

      // Update selected report and URL to match load behavior
      setSelectedReport(savedReport)
      setCurrentExportId(savedReport.id)
      originalReportRef.current = { name: savedReport.name, description: savedReport.description || '' }
      const slug = toReportSlug(savedReport.name)
      setSearchParams({ report: slug }, { replace: true })

      setReportName('')
      setReportDescription('')
      loadSavedReports()
    } catch (error: any) {
      console.error('Error saving report:', error)
      console.error('Error response:', error.response?.data)
      console.error('Error status:', error.response?.status)
      console.error('Error headers:', error.response?.headers)
      
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save report'
      toast.error(`Failed to save report: ${errorMessage}`)
    }
  }

  const handleLoadReport = (report: SavedReport, updateUrl: boolean = true) => {
    console.log('📂 Loading saved report:', report.name)
    console.log('📄 Report configuration:', JSON.stringify(report.configuration, null, 2))

    isLoadingReportRef.current = true
    originalReportRef.current = { name: report.name, description: report.description || '' }
    setSelectedReport(report)
    setCurrentExportId(report.id) // Set the current export ID to the loaded report's ID

    // Update URL with report slug if requested
    if (updateUrl) {
      const slug = toReportSlug(report.name)
      setSearchParams({ report: slug }, { replace: true })
    }

    // Load configuration
    const config = report.configuration
    if (config) {
      if (config.metrics) setSelectedMetrics(config.metrics)
      if (config.dimensions) setSelectedDimensions(config.dimensions)
      if (config.pivot) setSelectedPivot(config.pivot)
      setConditionals(config.conditional || '')

      // Handle push integration - check both 'push' (backend) and 'pushIntegration' (frontend) fields
      let pushIntValue = 'none'
      if (config.push && Array.isArray(config.push) && config.push.length > 0) {
        // Backend stores as array, take first value
        pushIntValue = config.push[0].toString()
      } else if (config.pushIntegration) {
        // Frontend stores as single value
        pushIntValue = config.pushIntegration.toString()
      }
      console.log('🔧 Setting push integration from saved report:', pushIntValue)
      setSelectedPushIntegration(pushIntValue)

      setSelectedFrequency(config.frequency || 'once')
      setColumnAggregations(config.column_aggregations || {})

      // Handle period - parse both relative and absolute dates
      if (config.period && Array.isArray(config.period) && config.period.length === 2) {
        const [start, end] = config.period
        const parsedFrom = parseDateFromReport(start)
        const parsedTo = parseDateFromReport(end)

        setDateRange({
          from: parsedFrom,
          to: parsedTo
        })
      }

      // Set pivot state (excluding our custom fields)
      const { metrics, dimensions, pivot, period, conditional, ...pivotConfigOnly } = config
      setPivotConfig({ ...pivotConfig, ...pivotConfigOnly })

      // Trigger auto-load after all state updates are batched and applied
      // This increments the trigger, which will fire the useEffect after React processes all state updates
      setAutoLoadTrigger(prev => prev + 1)

      // Clear the loading flag after state updates are applied
      setTimeout(() => {
        isLoadingReportRef.current = false
      }, 100)
    }
  }

  const handleDeleteReport = async () => {
    if (!selectedReport) return

    try {
      await api.delete(`/data/reports/${selectedReport.id}`)
      toast.success('Report deleted successfully')
      setShowDeleteConfirm(false)
      setSelectedReport(null)
      originalReportRef.current = null
      setCurrentExportId(null)
      setReportName('')
      setReportDescription('')
      // Clear report parameter from URL
      setSearchParams({}, { replace: true })
      loadSavedReports()
    } catch (error) {
      console.error('Error deleting report:', error)
      toast.error('Failed to delete report')
    }
  }

  const exportData = async () => {
    if (data.length === 0) {
      toast.error('No data to export')
      return
    }
    
    // If we have a pre-generated CSV URL from the optimizer, use it directly
    if (csvUrl) {
      console.log('📥 Downloading CSV from:', csvUrl)
      try {
        // Fetch the CSV with cache-busting headers to ensure we get fresh data
        const response = await fetch(csvUrl, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        })
        
        console.log('📥 Download response status:', response.status, response.statusText)
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        
        // Extract filename from URL or use timestamp-based name
        const urlParts = csvUrl.split('/')
        const originalFilename = urlParts[urlParts.length - 1].split('?')[0] // Remove query params
        const timestamp = new Date().getTime()
        const filename = `${originalFilename.replace('.csv', '')}_${timestamp}.csv`
        
        // Create download link
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // Clean up object URL
        setTimeout(() => URL.revokeObjectURL(url), 100)
        return
      } catch (error) {
        console.error('Error downloading CSV from URL, falling back to local generation:', error)
        // Fall through to generate CSV locally
      }
    }

    // Fallback: Generate CSV from frontend data if no pre-generated CSV available
    try {
      const headers = Object.keys(data[0])
      const csvContent = [
        headers.join(','),
        ...data.map(row => headers.map(header => {
          const value = row[header]
          // Escape commas and quotes in CSV
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`
          }
          return value
        }).join(','))
      ].join('\n')

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `report_${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error generating CSV:', error)
      toast.error('Failed to export report')
    }
  }

  const handleShareReport = async () => {
    let reportToShare = selectedReport

    // If no saved report, auto-save with the generated title
    if (!reportToShare) {
      const autoName = getReportTitle()

      if (!autoName || autoName === 'Report Data') {
        toast.error('Configure report filters first to share')
        return
      }

      try {
        // Build period array only if dates are specified
        const period: string[] | undefined = (dateRange?.from || dateRange?.to)
          ? [
              dateRange?.from ? formatDateForAPI(dateRange.from) : formatDateForAPI(dateRange.to!),
              dateRange?.to ? formatDateForAPI(dateRange.to) : formatDateForAPI(dateRange.from!)
            ]
          : undefined

        const configuration = {
          aggregatorName: pivotConfig.aggregatorName,
          rows: pivotConfig.rows,
          cols: pivotConfig.cols,
          vals: pivotConfig.vals,
          metrics: selectedMetrics,
          dimensions: selectedDimensions,
          pivot: selectedPivot,
          ...(period && { period }),
          ...(conditionals.trim() && { conditional: conditionals.trim() }),
          ...(selectedPushIntegration !== 'none' && {
            pushIntegration: selectedPushIntegration,
            push: [parseInt(selectedPushIntegration)]
          }),
          ...(selectedFrequency !== 'once' && { frequency: selectedFrequency })
        }

        const savePayload = {
          name: autoName,
          description: '',
          configuration
        }

        const response = await api.post('/data/reports', savePayload)
        reportToShare = response.data

        // Update state to reflect the saved report
        setSelectedReport(reportToShare)
        setCurrentExportId(reportToShare!.id)
        originalReportRef.current = { name: reportToShare!.name, description: reportToShare!.description || '' }
        const slug = toReportSlug(reportToShare!.name)
        setSearchParams({ report: slug }, { replace: true })
        loadSavedReports()

        toast.success('Report saved automatically')
      } catch (error) {
        console.error('Error auto-saving report:', error)
        toast.error('Failed to save report for sharing')
        return
      }
    }

    try {
      // Generate shareable URL with org slug and report slug
      const slug = reportToShare!.name.toLowerCase().replace(/\s+/g, '_')
      const shareUrl = `${window.location.origin}/${orgSlug}/reports?report=${encodeURIComponent(slug)}`
      await navigator.clipboard.writeText(shareUrl)
      toast.success('Report URL copied to clipboard')
    } catch (error) {
      toast.error('Failed to copy URL')
    }
  }

  // Generate display options for dropdowns
  const metricOptions: MultiSelectOption[] = availableMetrics.map(m => ({
    label: m.name,
    value: m.slug || m.name
  })).sort((a, b) => a.label.localeCompare(b.label))

  // Meta dimensions for time-based grouping
  const metaDimensionOptions: MultiSelectOption[] = [
    { label: 'days', value: 'days' },
    { label: 'months', value: 'months' },
    { label: 'weeks', value: 'weeks' },
    { label: 'years', value: 'years' },
  ]

  const dimensionOptions: MultiSelectOption[] = [
    ...metaDimensionOptions,
    ...availableDimensions.map(d => ({
      label: d.name,
      value: d.slug || d.name
    }))
  ].sort((a, b) => a.label.localeCompare(b.label))

  const pivotOptions: MultiSelectOption[] = dimensionOptions.filter(d =>
    !selectedDimensions.includes(d.value)
  )

  // Check if Run Report button should be enabled
  const canApplyFilters = selectedMetrics.length > 0
  
  // Perform frontend pivot transformation
  const createPivotTable: { rows: any[], columns: string[], pivotColumns: string[] } = useMemo(() => {
    if (data.length === 0) return { rows: [], columns: [], pivotColumns: [] }
    
    // If no pivot dimensions OR no row dimensions, show data as-is
    if (appliedPivot.length === 0 || appliedDimensions.length === 0) {
      return {
        rows: data,
        columns: Object.keys(data[0] || {}),
        pivotColumns: []
      }
    }
    
    // FRONTEND PIVOT LOGIC
    // Step 1: Collect all unique pivot values
    const pivotValues = new Set<string>()
    data.forEach(row => {
      const pivotKey = selectedPivot.map(field => row[field] || 'N/A').join(' | ')
      pivotValues.add(pivotKey)
    })
    
    // Preserve order of first appearance in data (don't sort alphabetically)
    const pivotArray = Array.from(pivotValues)
    
    // Step 2: Group data by row dimensions
    const grouped: { [key: string]: any } = {}
    
    data.forEach(row => {
      // Create row key from row dimensions
      const rowKey = appliedDimensions.length > 0
        ? appliedDimensions.map(field => row[field] || 'N/A').join(' | ')
        : 'aggregate'
      
      // Create pivot key
      const pivotKey = appliedPivot.map(field => row[field] || 'N/A').join(' | ')
      
      // Initialize row group
      if (!grouped[rowKey]) {
        grouped[rowKey] = {
          // Add row dimension values
          ...appliedDimensions.reduce((acc, field) => {
            acc[field] = row[field]
            return acc
          }, {} as any),
          // Initialize pivot columns
          ...pivotArray.reduce((acc, pKey) => {
            appliedMetrics.forEach(metric => {
              const colName = appliedMetrics.length > 1 ? `${pKey} - ${metric}` : pKey
              acc[colName] = 0
            })
            return acc
          }, {} as any)
        }
      }
      
      // Aggregate metrics into pivot columns
      appliedMetrics.forEach(metric => {
        const colName = appliedMetrics.length > 1 ? `${pivotKey} | ${metric}` : pivotKey
        const value = parseFloat(row[metric]) || 0
        grouped[rowKey][colName] = (grouped[rowKey][colName] || 0) + value
      })
    })

    // Step 3: Create final structure
    const finalRows = Object.values(grouped)
    const baseColumns = appliedDimensions
    const pivotColumns = pivotArray.flatMap(pivotKey =>
      appliedMetrics.length > 1
        ? appliedMetrics.map(metric => `${pivotKey} | ${metric}`)
        : [pivotKey]
    )
    const allColumns = [...baseColumns, ...pivotColumns]
    
    return {
      rows: finalRows,
      columns: allColumns,
      pivotColumns: pivotColumns
    }
  }, [data, appliedDimensions, appliedPivot, appliedMetrics, pivotConfig.aggregatorName])

  // Filter out columns where all values are null/empty/zero
  const visibleColumns = useMemo(() => {
    if (!hideEmptyColumns) {
      return createPivotTable.columns
    }

    return createPivotTable.columns.filter(col => {
      // Check if any row has a meaningful (non-null, non-empty, non-zero) value for this column
      return createPivotTable.rows.some(row => {
        const value = row[col]
        // Skip null, undefined, empty string, or dash
        if (value === null || value === undefined || value === '' || value === '-') {
          return false
        }
        // Skip numeric zero (displays as '-' in the table)
        if (typeof value === 'number' && value === 0) {
          return false
        }
        // Skip string zero
        if (typeof value === 'string' && !isNaN(parseFloat(value)) && parseFloat(value) === 0) {
          return false
        }
        return true
      })
    })
  }, [createPivotTable.columns, createPivotTable.rows, hideEmptyColumns])

  // Sort rows based on current sort configuration
  const sortedRows = useMemo(() => {
    if (!sortConfig.column || !sortConfig.direction) {
      return createPivotTable.rows
    }

    return [...createPivotTable.rows].sort((a, b) => {
      const aVal = a[sortConfig.column!]
      const bVal = b[sortConfig.column!]

      // Handle null/undefined values - push them to the end
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return 1
      if (bVal == null) return -1

      // Numeric comparison (skip date strings like "2025-01-15" where parseFloat extracts just the year)
      const aIsDate = typeof aVal === 'string' && /^\d{4}-\d{2}-\d{2}/.test(aVal)
      const bIsDate = typeof bVal === 'string' && /^\d{4}-\d{2}-\d{2}/.test(bVal)

      if (!aIsDate && !bIsDate) {
        const aNum = typeof aVal === 'number' ? aVal : parseFloat(aVal)
        const bNum = typeof bVal === 'number' ? bVal : parseFloat(bVal)

        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum
        }
      }

      // String comparison
      const aStr = String(aVal).toLowerCase()
      const bStr = String(bVal).toLowerCase()
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
  }, [createPivotTable.rows, sortConfig])

  // Handle column header click for sorting
  const handleSortClick = (column: string) => {
    setSortConfig(prev => {
      if (prev.column !== column) {
        return { column, direction: 'asc' }
      }
      if (prev.direction === 'asc') {
        return { column, direction: 'desc' }
      }
      return { column: null, direction: null }
    })
  }

  // Build lookup maps for metric formulas and aggregation from primitives
  const metricInfoMap = useMemo(() => {
    const map: Record<string, { formula?: string; aggregation: string }> = {}
    availableMetrics.forEach(m => {
      const info = { formula: m.formula || undefined, aggregation: m.aggregation || 'sum' }
      map[m.name] = info
      if (m.slug) map[m.slug] = info
    })
    return map
  }, [availableMetrics])

  // Calculate totals for metric columns
  const calculateTotals = useMemo(() => {
    if (!showTotals || createPivotTable.rows.length === 0) return null

    const totalsRow: Record<string, any> = {}

    // Helper: collect numeric values from a column across all rows
    const columnValues = (col: string): number[] => {
      const values: number[] = []
      createPivotTable.rows.forEach(row => {
        const value = row[col]
        const num = typeof value === 'string' ? parseFloat(value) :
                    typeof value === 'number' ? value : NaN
        if (!isNaN(num) && isFinite(num)) values.push(num)
      })
      return values
    }

    // Helper: sum a column (default aggregation)
    const sumColumn = (col: string): number => {
      return columnValues(col).reduce((a, b) => a + b, 0)
    }

    // Helper: aggregate a column using the user-chosen method for the total row
    const aggregateColumn = (col: string): number => {
      // Check for per-column override; find the base metric name for pivot columns
      const matchingMetric = appliedMetrics.find(metric => col === metric || col.endsWith(` | ${metric}`))
      const method = columnAggregations[col] || (matchingMetric ? columnAggregations[matchingMetric] : undefined) || 'sum'
      const values = columnValues(col)
      if (values.length === 0) return 0
      switch (method) {
        case 'avg': return values.reduce((a, b) => a + b, 0) / values.length
        case 'min': return Math.min(...values)
        case 'max': return Math.max(...values)
        default:    return values.reduce((a, b) => a + b, 0)
      }
    }

    // Helper: evaluate a formula string with a context of variable values
    const evaluateFormula = (formula: string, context: Record<string, number>): number | null => {
      try {
        const varNames = formula.match(/\b[a-zA-Z_]\w*\b/g) || []
        for (const v of varNames) {
          if (!(v in context)) return null
        }
        let expr = formula
        // Sort by length descending to avoid partial replacements (e.g. "ua_spent" before "ua")
        const sortedVars = Object.keys(context).sort((a, b) => b.length - a.length)
        for (const name of sortedVars) {
          expr = expr.replace(new RegExp(`\\b${name}\\b`, 'g'), String(context[name]))
        }
        const result = Function(`"use strict"; return (${expr})`)()
        return typeof result === 'number' && isFinite(result) ? result : null
      } catch {
        return null
      }
    }

    // Identify base vs calculated metric columns
    const calculatedColumns: { col: string; metric: string; formula: string }[] = []

    createPivotTable.columns.forEach(col => {
      const isDimensionColumn = appliedDimensions.includes(col)
      const isMetricColumn = !isDimensionColumn && (
        appliedMetrics.includes(col) ||
        appliedMetrics.some(metric => col.includes(metric)) ||
        createPivotTable.pivotColumns?.includes(col) ||
        (createPivotTable.pivotColumns?.length === 0 && appliedMetrics.length > 0 &&
         createPivotTable.rows.length > 0 &&
         (typeof createPivotTable.rows[0][col] === 'number' ||
          (typeof createPivotTable.rows[0][col] === 'string' && !isNaN(parseFloat(createPivotTable.rows[0][col])))))
      )

      if (isDimensionColumn) {
        totalsRow[col] = totalsRow[col] === undefined ? 'Total' : totalsRow[col]
      } else if (isMetricColumn) {
        const matchingMetric = appliedMetrics.find(metric => col === metric || col.endsWith(` | ${metric}`))
        const info = matchingMetric ? metricInfoMap[matchingMetric] : undefined
        const formula = info?.formula

        // If user has set a custom aggregation for this column, use it directly
        // (even for formula metrics — the override takes precedence)
        const hasCustomAgg = columnAggregations[col] || (matchingMetric ? columnAggregations[matchingMetric] : undefined)
        if (hasCustomAgg) {
          totalsRow[col] = aggregateColumn(col)
        } else if (formula && !formula.includes('~') && !formula.toLowerCase().includes('ltv_function')) {
          calculatedColumns.push({ col, metric: matchingMetric!, formula })
        } else {
          totalsRow[col] = aggregateColumn(col)
        }
      } else {
        totalsRow[col] = ''
      }
    })

    // Recursively resolve a metric's total value, following formula chains
    // e.g. roas -> total_revenue -> (ad_revenue + iap_revenue)
    const resolved: Record<string, number | null> = {}
    const resolveMetric = (metricName: string, pivotPrefix: string, visiting: Set<string>): number | null => {
      const key = `${pivotPrefix}${metricName}`
      if (key in resolved) return resolved[key]

      // Prevent circular dependencies
      if (visiting.has(metricName)) return null
      visiting.add(metricName)

      // Check if already computed in totalsRow (base metrics)
      const colName = pivotPrefix ? `${pivotPrefix}${metricName}` : metricName
      if (colName in totalsRow && totalsRow[colName] !== '' && totalsRow[colName] !== 'Total') {
        const num = typeof totalsRow[colName] === 'number' ? totalsRow[colName] : parseFloat(totalsRow[colName])
        if (!isNaN(num) && isFinite(num)) {
          resolved[key] = num
          return num
        }
      }

      // Check if it exists as a data column we can sum (even if not a selected metric)
      const hasColumn = createPivotTable.rows.some(row => row[colName] !== undefined)
      if (hasColumn) {
        const sum = sumColumn(colName)
        resolved[key] = sum
        return sum
      }

      // Try to resolve via formula from the model
      const info = metricInfoMap[metricName]
      if (!info?.formula || info.formula.includes('~') || info.formula.toLowerCase().includes('ltv_function')) {
        resolved[key] = null
        return null
      }

      const depVars = info.formula.match(/\b[a-zA-Z_]\w*\b/g) || []
      const context: Record<string, number> = {}
      for (const dep of depVars) {
        const depValue = resolveMetric(dep, pivotPrefix, new Set(visiting))
        if (depValue === null) {
          resolved[key] = null
          return null
        }
        context[dep] = depValue
      }

      const result = evaluateFormula(info.formula, context)
      resolved[key] = result
      return result
    }

    // Compute calculated metrics using recursive resolution
    for (const { col, metric, formula } of calculatedColumns) {
      const pivotPrefix = col !== metric ? col.slice(0, col.length - metric.length) : ''
      const formulaVars = formula.match(/\b[a-zA-Z_]\w*\b/g) || []

      const context: Record<string, number> = {}
      let allDepsAvailable = true
      for (const varName of formulaVars) {
        const depValue = resolveMetric(varName, pivotPrefix, new Set())
        if (depValue === null) {
          allDepsAvailable = false
          break
        }
        context[varName] = depValue
      }

      if (allDepsAvailable) {
        const result = evaluateFormula(formula, context)
        totalsRow[col] = result !== null ? result : sumColumn(col)
      } else {
        totalsRow[col] = sumColumn(col)
      }
    }

    return totalsRow
  }, [createPivotTable, appliedDimensions, appliedMetrics, showTotals, metricInfoMap, columnAggregations])

  // Generate dynamic report title
  const getReportTitle = () => {
    // If a saved report is loaded, use its name
    if (selectedReport) {
      return selectedReport.name
    }

    // Otherwise, generate title from applied configuration
    const firstDimension = appliedDimensions[0] || 'Dimension'
    const firstPivot = appliedPivot[0] || 'Pivot'
    const firstMetric = appliedMetrics[0] || 'Metric'

    // Only show parts that are actually configured
    if (appliedDimensions.length === 0 && appliedPivot.length === 0 && appliedMetrics.length === 0) {
      return 'Report Data'
    }

    const parts = []
    if (appliedDimensions.length > 0) parts.push(firstDimension)
    if (appliedPivot.length > 0) parts.push(firstPivot)
    if (appliedMetrics.length > 0) parts.push(firstMetric)

    return parts.join('_')
  }

  // Format number with optional "num" formatting (2 decimals with K/M/B suffixes)
  const formatNumber = (value: number): string => {
    if (value === 0) return '-'

    if (showNumFormatted) {
      const absValue = Math.abs(value)
      if (absValue >= 1e9) {
        return (value / 1e9).toFixed(2) + 'B'
      } else if (absValue >= 1e6) {
        return (value / 1e6).toFixed(2) + 'M'
      } else if (absValue >= 1e3) {
        return (value / 1e3).toFixed(2) + 'K'
      }
      return value.toFixed(2)
    }

    // Show up to 4 decimals when formatting is off
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 4
    })
  }

  // Generate report subtitle with period and conditionals
  const getReportSubtitle = () => {
    if (data.length === 0) {
      return 'Select metrics and dimensions to generate a report'
    }

    // Format dates
    const formatDate = (date: Date | string | undefined): string => {
      if (!date) return 'N/A'

      if (typeof date === 'string') {
        // Handle relative dates (d0, d-7, etc.)
        if (date.match(/^d-?\d+$/)) {
          return date
        }
        // Try parsing as date
        const parsed = new Date(date)
        if (!isNaN(parsed.getTime())) {
          return parsed.toLocaleDateString()
        }
        return date
      }

      return date.toLocaleDateString()
    }

    const startDate = formatDate(dateRange?.from)
    const endDate = formatDate(dateRange?.to)

    let subtitle = `Period: ${startDate} to ${endDate}`

    // Add conditionals if present
    if (conditionals.trim()) {
      subtitle += ` | Conditionals: ${conditionals.trim()}`
    }

    return subtitle
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-[calc(100vh-6rem)] gap-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <DropdownMenu onOpenChange={(open) => {
            if (open && showLoadReportsHint) {
              setShowLoadReportsHint(false)
              localStorage.setItem('glaium-reports-visited', 'true')
            }
            if (!open) setReportSearchQuery('')
          }}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                title="Load Report"
                className="relative"
              >
                <FolderOpen className="h-4 w-4" />
                {showLoadReportsHint && (
                  <span className="absolute inset-0 rounded-md bg-stone-400 opacity-75 animate-ping" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-96">
              <div className="flex items-center border-b px-2 py-1.5">
                <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                <input
                  type="text"
                  placeholder="Search reports..."
                  value={reportSearchQuery}
                  className="h-7 w-full border-0 bg-transparent p-0 text-sm focus:outline-none focus:ring-0"
                  onChange={(e) => setReportSearchQuery(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                />
              </div>
              <div className="max-h-[300px] overflow-y-auto p-1">
                {!reportSearchQuery && (
                  <DropdownMenuItem
                    onClick={() => {
                      setSelectedReport(null)
                      originalReportRef.current = null
                      setCurrentExportId(null)
                      setSelectedMetrics([])
                      setSelectedDimensions([])
                      setSelectedPivot([])
                      setConditionals('')
                      setConditionalError(null)
                      setSelectedPushIntegration('none')
                      setSelectedFrequency('once')
                      setDateRange({})
                      setData([])
                      setHasRunReport(false)
                      setCsvUrl(null)
                      setAppliedMetrics([])
                      setAppliedDimensions([])
                      setAppliedPivot([])
                      setSearchParams({}, { replace: true })
                    }}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    New Report
                  </DropdownMenuItem>
                )}
                {savedReports.length > 0 && !reportSearchQuery && <DropdownMenuSeparator />}
                {savedReports
                  .filter((report) => report.name.toLowerCase().includes(reportSearchQuery.toLowerCase()))
                  .map((report) => {
                    const isSelected = selectedReportId !== null && report.id === selectedReportId
                    return (
                      <DropdownMenuItem
                        key={report.id}
                        onClick={() => handleLoadReport(report)}
                        className={isSelected ? 'bg-accent' : ''}
                      >
                        {isSelected && <Check className="mr-2 h-4 w-4" />}
                        {!isSelected && <span className="mr-6" />}
                        <span className="truncate">{report.name}</span>
                      </DropdownMenuItem>
                    )
                  })}
                {savedReports.length === 0 && (
                  <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                    No saved reports
                  </div>
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="relative inline-block">
                <span
                  className="invisible whitespace-pre text-2xl font-bold tracking-tight px-2"
                  aria-hidden="true"
                >
                  {selectedReport?.name || reportName || getReportTitle() || ' '}
                </span>
                <input
                  type="text"
                  value={selectedReport?.name || reportName || getReportTitle()}
                  onChange={(e) => {
                    if (selectedReport) {
                      setSelectedReport({ ...selectedReport, name: e.target.value })
                    } else {
                      setReportName(e.target.value)
                    }
                  }}
                  className="absolute inset-0 text-2xl font-bold tracking-tight text-slate-900 h-auto py-0 px-0 border border-transparent rounded-md hover:border-input focus:border-input focus:px-2 focus:outline-none bg-transparent cursor-text"
                />
              </div>
              {(hasUnsavedChanges && selectedReport) || (!selectedReport && selectedMetrics.length > 0) ? (
                <button
                  onClick={handleSaveReport}
                  className={`px-2 py-1 rounded text-sm font-medium transition-colors ${
                    hasUnsavedChanges && selectedReport
                      ? 'bg-amber-100 hover:bg-amber-200 text-amber-700'
                      : 'bg-stone-100 hover:bg-stone-200 text-stone-600'
                  }`}
                  title={hasUnsavedChanges ? "Save unsaved changes" : "Save report"}
                >
                  Save
                </button>
              ) : null}
            </div>
            <input
              type="text"
              value={selectedReport?.description ?? reportDescription}
              onChange={(e) => {
                if (selectedReport) {
                  setSelectedReport({ ...selectedReport, description: e.target.value })
                } else {
                  setReportDescription(e.target.value)
                }
              }}
              placeholder="Add a description..."
              className="text-muted-foreground text-sm h-auto py-0 px-0 border border-transparent rounded hover:border-input focus:border-input focus:px-1 focus:outline-none bg-transparent cursor-text w-full"
            />
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              title="Report Options"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Push Settings</DropdownMenuLabel>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Send className="h-4 w-4 mr-2" />
                Integration
                {selectedPushIntegration !== 'none' && (
                  <span className="ml-auto text-xs text-muted-foreground">
                    {pushIntegrations.find(i => i.id?.toString() === selectedPushIntegration)?.name || 'Selected'}
                  </span>
                )}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup value={selectedPushIntegration} onValueChange={(value) => {
                  setSelectedPushIntegration(value)
                  if (value === 'none') {
                    setSelectedFrequency('once')
                  }
                }}>
                  <DropdownMenuRadioItem value="none">None</DropdownMenuRadioItem>
                  {pushIntegrations.map((integration) => (
                    <DropdownMenuRadioItem key={integration.id} value={integration.id?.toString() || 'none'}>
                      {integration.name}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            {selectedPushIntegration !== 'none' && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <Clock className="h-4 w-4 mr-2" />
                  Frequency
                  <span className="ml-auto text-xs text-muted-foreground capitalize">
                    {selectedFrequency}
                  </span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuRadioGroup value={selectedFrequency} onValueChange={setSelectedFrequency}>
                    <DropdownMenuRadioItem value="once">Once</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="hourly">Hourly</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="daily">Daily</DropdownMenuRadioItem>
                    <DropdownMenuRadioItem value="weekly">Weekly</DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={(e) => e.preventDefault()}
              className="flex items-center justify-between"
            >
              <span>Auto-run on Load</span>
              <Switch
                checked={autoLoadReports}
                onCheckedChange={(checked) => {
                  setAutoLoadReports(checked)
                  localStorage.setItem('glaium-reports-autoload', String(checked))
                }}
                className="ml-2 h-5 w-9 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input [&>span]:h-4 [&>span]:w-4 [&>span]:data-[state=checked]:translate-x-4"
              />
            </DropdownMenuItem>
            {selectedReport && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Report
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Filters */}
      <Card className="shadow-xs p-6 flex-shrink-0">
        <Tabs value={isQuickMode ? 'smart' : 'advanced'} onValueChange={(value) => setIsQuickMode(value === 'smart')} className="space-y-4">
          {/* Mode Toggle + Run Report */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pb-2">
            <TabsList className="self-start sm:self-auto">
              <TabsTrigger value="smart">
                <Sparkles className="mr-2 h-4 w-4" />
                Smart
              </TabsTrigger>
              <TabsTrigger value="advanced">
                <SlidersHorizontal className="mr-2 h-4 w-4" />
                Advanced
              </TabsTrigger>
            </TabsList>
            <div className="flex items-center gap-2 self-end sm:self-auto">
              <Button
                onClick={loadData}
                disabled={!canApplyFilters}
                className="min-w-[140px]"
              >
                {loadingData ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading...</>
                ) : (
                  <><Play className="mr-2 h-4 w-4" /> Run</>
                )}
              </Button>
            </div>
          </div>

          {/* Smart Mode - Smart Input */}
          <TabsContent value="smart" className="space-y-2 mt-0">
            <SmartReportInput
                metricOptions={metricOptions}
                dimensionOptions={dimensionOptions}
                initialMetrics={selectedMetrics}
                initialDimensions={selectedDimensions}
                initialPivot={selectedPivot}
                initialDateRange={dateRange.from || dateRange.to ? dateRange : null}
                initialConditionals={conditionals}
                onQueryParsed={(parsed) => {
                  setSelectedMetrics(parsed.metrics)
                  setSelectedDimensions(parsed.dimensions)
                  setSelectedPivot(parsed.pivot)
                  if (parsed.dateRange) {
                    setDateRange({
                      from: parsed.dateRange.from,
                      to: parsed.dateRange.to
                    })
                  } else {
                    setDateRange({})
                  }
                  setConditionals(parsed.conditionals)
                }}
                onFetchDimensionValues={async (dimension) => {
                  // Use current date range or fallback to last 30 days
                  const period = dateRange?.from && dateRange?.to
                    ? [formatDateForAPI(dateRange.from), formatDateForAPI(dateRange.to)]
                    : ['d-30', 'd0']

                  const response = await api.post('/data/retrieve', {
                    organization_id: currentOrganization?.id,
                    output: 'json', // Get data directly (faster, no CSV overhead)
                    metrics: [],
                    dimensions: [dimension],
                    period,
                    limit: 100
                  })

                  // Extract unique values from the response
                  const data = response.data.data || []
                  console.log('📋 Dimension values response:', { dimension, dataLength: data.length, sample: data[0] })

                  // Find the key that matches the dimension (case-insensitive)
                  const dimKey = data.length > 0
                    ? Object.keys(data[0]).find(k => k.toLowerCase() === dimension.toLowerCase()) || dimension
                    : dimension

                  const values = [...new Set(data.map((row: any) => row[dimKey]).filter(Boolean))]
                  console.log('📋 Extracted values:', values)
                  return values as string[]
                }}
              />
          </TabsContent>

          {/* Advanced Mode - Full Controls */}
          <TabsContent value="advanced" className="space-y-4 mt-0">
          {/* Row 1: Metrics, Dimensions, Pivot */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 1.1 Metrics */}
            <div className="space-y-2">
              <Label className="text-sm font-medium h-5 flex items-center">Metrics</Label>
              <OrderedMultiSelect
                options={metricOptions}
                value={selectedMetrics}
                onValueChange={setSelectedMetrics}
                placeholder="Select metrics..."
                searchPlaceholder="Search metrics..."
                emptyText="No metrics found."
                maxHeight="200px"
              />
            </div>

            {/* 1.2 Dimensions */}
            <div className="space-y-2">
              <Label className="text-sm font-medium h-5 flex items-center">Dimensions</Label>
              <OrderedMultiSelect
                options={dimensionOptions}
                value={selectedDimensions}
                onValueChange={setSelectedDimensions}
                placeholder="Select dimensions..."
                searchPlaceholder="Search dimensions..."
                emptyText="No dimensions found."
                maxHeight="200px"
              />
            </div>

            {/* 1.3 Pivot */}
            <div className="space-y-2">
              <Label className="text-sm font-medium h-5 flex items-center">
                Pivot <span className="text-xs text-muted-foreground">(Optional)</span>
              </Label>
              <OrderedMultiSelect
                options={pivotOptions}
                value={selectedPivot}
                onValueChange={setSelectedPivot}
                placeholder="Select pivot dimensions..."
                searchPlaceholder="Search pivot dimensions..."
                emptyText="No available pivot dimensions."
                maxHeight="200px"
              />
            </div>
          </div>

          {/* Row 2: Start Date, End Date, Conditionals */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 2.1 Start Date */}
            <div className="space-y-2">
              <Label className="text-sm font-medium h-5 flex items-center">Start Date</Label>
              <SmartDateInput
                id="start-date"
                label=""
                value={dateRange?.from}
                onChange={(date) => setDateRange(prev => ({ ...prev, from: date }))}
                placeholder="yesterday, last week, 2024-01-01, d-1"
              />
            </div>

            {/* 2.2 End Date */}
            <div className="space-y-2">
              <Label className="text-sm font-medium h-5 flex items-center">End Date</Label>
              <SmartDateInput
                id="end-date"
                label=""
                value={dateRange?.to}
                onChange={(date) => setDateRange(prev => ({ ...prev, to: date }))}
                placeholder="today, tomorrow, 2024-12-31, d0"
                fromDate={dateRange?.from}
              />
            </div>

            {/* 2.3 Conditionals */}
            <div className="space-y-2">
              <Label className="text-sm font-medium h-5 flex items-center gap-2">
                Conditionals <span className="text-xs text-muted-foreground">(Optional)</span>
                <HelpCircle
                  className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer"
                  onClick={handleConditionalHelp}
                />
              </Label>
              <Input
                value={conditionals}
                onChange={(e) => {
                  setConditionals(e.target.value)
                  if (conditionalError) setConditionalError(null)
                }}
                onBlur={(e) => {
                  const value = e.target.value.trim()
                  if (!value) {
                    setConditionalError(null)
                    return
                  }
                  // Validate and normalize
                  const metricOpts = availableMetrics.map(m => ({ label: m.name, value: m.name }))
                  const dimOpts = availableDimensions.map(d => ({ label: d.name, value: d.name }))
                  const validation = validateConditional(value, metricOpts, dimOpts)
                  if (!validation.valid) {
                    setConditionalError(validation.error || 'Invalid conditional')
                  } else {
                    setConditionalError(null)
                    // Normalize (add quotes to string values)
                    setConditionals(normalizeConditional(value))
                  }
                }}
                placeholder="e.g., integration_id in (2013,2014)"
                className={`text-sm ${conditionalError ? 'border-destructive' : ''}`}
              />
              {conditionalError && (
                <p className="text-xs text-destructive">{conditionalError}</p>
              )}
            </div>
          </div>
          </TabsContent>
        </Tabs>

      </Card>

      {/* Data Table - Normal View */}
      {!isMaximized && (
        <Card className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {data.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              {hasRunReport ? (
                <>
                  <p className="text-lg text-muted-foreground mb-2">
                    No data found!
                  </p>
                  <p className="text-sm text-muted-foreground max-w-md mx-auto">
                    query: {appliedMetrics.length > 0 ? appliedMetrics.join(' ') : ''}
                    {appliedDimensions.length > 0 ? ` ${appliedDimensions.join(' ')}` : ''}
                    {appliedPivot.length > 0 ? ` pivot ${appliedPivot.join(' ')}` : ''}
                    {dateRange?.from || dateRange?.to ? ` ${dateRange?.from ? formatDateForAPI(dateRange.from) : ''}..${dateRange?.to ? formatDateForAPI(dateRange.to) : ''}` : ''}
                    {conditionals.trim() ? ` if ${conditionals.trim()}` : ''}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-lg text-muted-foreground mb-2">
                    No data to display
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Select metrics and dimensions above, then click Run to generate
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="h-full flex flex-col">
              {/* Settings bar above table */}
              <div className="flex justify-end gap-1 px-2 py-1 border-b bg-muted/30">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="p-1 rounded hover:bg-muted">
                      <Settings className="h-4 w-4 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuLabel>Table Settings</DropdownMenuLabel>
                    <div className="px-2 py-1.5 flex items-center justify-between">
                      <span className="text-sm">Short Numbers</span>
                      <Switch checked={showNumFormatted} onCheckedChange={setShowNumFormatted} className="scale-75" />
                    </div>
                    <div className="px-2 py-1.5 flex items-center justify-between">
                      <span className="text-sm">Show Totals</span>
                      <Switch checked={showTotals} onCheckedChange={setShowTotals} className="scale-75" />
                    </div>
                    <div className="px-2 py-1.5 flex items-center justify-between">
                      <span className="text-sm">Hide Empty Columns</span>
                      <Switch checked={hideEmptyColumns} onCheckedChange={setHideEmptyColumns} className="scale-75" />
                    </div>
                    <div className="px-2 py-1.5 flex items-center justify-between">
                      <span className="text-sm">Freeze Dimensions</span>
                      <Switch checked={freezeDimensions} onCheckedChange={setFreezeDimensions} className="scale-75" />
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
                <button className="p-1 rounded hover:bg-muted disabled:opacity-50" onClick={exportData} disabled={data.length === 0} title="Download Report">
                  <Download className="h-4 w-4 text-muted-foreground" />
                </button>
                <button className="p-1 rounded hover:bg-muted" onClick={handleShareReport} title="Share Report">
                  <Share2 className="h-4 w-4 text-muted-foreground" />
                </button>
                <button className="p-1 rounded hover:bg-muted" onClick={() => setIsMaximized(true)} title="Fullscreen">
                  <Maximize2 className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
              {/* Desktop: Right-click context menu */}
              <ContextMenu>
                <ContextMenuTrigger asChild>
                  <div className="flex-1 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {visibleColumns.map((col, colIndex) => {
                      const isPivotColumn = createPivotTable.pivotColumns?.includes(col) || false
                      const isDimensionColumn = appliedDimensions.includes(col)
                      const dimensionIndex = isDimensionColumn ? appliedDimensions.indexOf(col) : -1
                      // Dimension columns should never be treated as metrics, even if they contain numbers
                      const isMetricColumn = !isDimensionColumn && (
                        appliedMetrics.includes(col) ||
                        appliedMetrics.some(metric => col.includes(metric)) ||
                        isPivotColumn ||
                        // For non-pivot reports, check if column is a metric by checking if it's numeric (string or number)
                        (createPivotTable.pivotColumns?.length === 0 && appliedMetrics.length > 0 &&
                         sortedRows.length > 0 &&
                         (typeof sortedRows[0][col] === 'number' ||
                          (typeof sortedRows[0][col] === 'string' && !isNaN(parseFloat(sortedRows[0][col])))))
                      )

                      let backgroundClass = 'bg-zinc-50'
                      let textClass = 'text-zinc-700'
                      let borderClass = ''

                      if (isPivotColumn) {
                        backgroundClass = 'bg-zinc-300'
                        textClass = 'text-zinc-900'
                        borderClass = 'border-l border-zinc-500'
                      } else if (isDimensionColumn) {
                        backgroundClass = 'bg-zinc-100'
                        textClass = 'text-zinc-800'
                        borderClass = dimensionIndex === 0 ? '' : 'border-l border-zinc-300'
                      } else if (isMetricColumn) {
                        backgroundClass = 'bg-zinc-200'
                        textClass = 'text-zinc-800'
                        borderClass = 'border-l border-zinc-400'
                      }

                      // All header cells are sticky top, dimension columns also sticky left when frozen
                      const stickyStyle = {
                        position: 'sticky' as const,
                        top: 0,
                        ...(isDimensionColumn && freezeDimensions ? {
                          left: dimensionIndex * 120,
                          zIndex: 40 - dimensionIndex, // Higher z-index for dimension columns in header
                        } : {
                          zIndex: 20, // Lower z-index for non-dimension header columns
                        })
                      }

                      const isSorted = sortConfig.column === col

                      return (
                        <TableHead
                          key={col}
                          className={`font-medium ${backgroundClass} ${textClass} ${borderClass} cursor-pointer hover:bg-zinc-200 select-none`}
                          style={{
                            textAlign: isMetricColumn ? 'right' : 'left',
                            direction: 'ltr',
                            minWidth: isDimensionColumn && freezeDimensions ? 120 : undefined,
                            ...stickyStyle
                          }}
                          onClick={() => handleSortClick(col)}
                        >
                          <div className={`flex items-center gap-1 ${isMetricColumn ? 'justify-end' : ''}`}>
                            <span>{col}</span>
                            {isSorted && (
                              sortConfig.direction === 'asc'
                                ? <ArrowUp className="h-4 w-4" />
                                : <ArrowDown className="h-4 w-4" />
                            )}
                          </div>
                        </TableHead>
                      )
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRows.map((row, index) => (
                    <TableRow key={index}>
                      {visibleColumns.map((col, colIndex) => {
                        const isPivotColumn = createPivotTable.pivotColumns?.includes(col) || false
                        const isDimensionColumn = appliedDimensions.includes(col)
                        const dimensionIndex = isDimensionColumn ? appliedDimensions.indexOf(col) : -1
                        // Dimension columns should never be treated as metrics, even if they contain numbers
                        const isMetricColumn = !isDimensionColumn && (
                          appliedMetrics.includes(col) ||
                          appliedMetrics.some(metric => col.includes(metric)) ||
                          isPivotColumn ||
                          // For non-pivot reports, check if column is a metric by checking if it's numeric
                          (createPivotTable.pivotColumns?.length === 0 && appliedMetrics.length > 0 &&
                           sortedRows.length > 0 && typeof sortedRows[0][col] === 'number')
                        )

                        const value = row[col]

                        let cellClass = ''

                        if (isPivotColumn) {
                          cellClass = 'border-l border-zinc-500 bg-zinc-300/20'
                        } else if (isDimensionColumn) {
                          cellClass = dimensionIndex === 0 ? 'bg-zinc-100' : 'border-l border-zinc-300 bg-zinc-100'
                        } else if (isMetricColumn) {
                          cellClass = 'border-l border-zinc-400 bg-white'
                        }

                        // Sticky style for dimension columns when frozen
                        const stickyStyle = isDimensionColumn && freezeDimensions ? {
                          position: 'sticky' as const,
                          left: dimensionIndex * 120,
                          zIndex: 10 - dimensionIndex,
                        } : {}

                        return (
                          <TableCell
                            key={col}
                            className={cellClass}
                            style={{
                              textAlign: isMetricColumn ? 'right' : 'left',
                              direction: 'ltr',
                              minWidth: isDimensionColumn && freezeDimensions ? 120 : undefined,
                              ...stickyStyle
                            }}
                          >
                            {isMetricColumn && (typeof value === 'number' || (typeof value === 'string' && value !== '' && !isNaN(Number(value))))
                              ? formatNumber(Number(value))
                              : (value || '-')}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  ))}
                  {calculateTotals && (
                    <TableRow className="border-t-2 border-slate-200 bg-slate-50 font-semibold sticky bottom-0 z-10">
                      {visibleColumns.map((col, colIndex) => {
                        const isDimensionColumn = appliedDimensions.includes(col)
                        const dimensionIndex = isDimensionColumn ? appliedDimensions.indexOf(col) : -1
                        // Dimension columns should never be treated as metrics, even if they contain numbers
                        const isMetricColumn = !isDimensionColumn && (
                          appliedMetrics.includes(col) ||
                          appliedMetrics.some(metric => col.includes(metric)) ||
                          createPivotTable.pivotColumns?.includes(col) ||
                          // For non-pivot reports, check if column is a metric by checking if it's numeric
                          (createPivotTable.pivotColumns?.length === 0 && appliedMetrics.length > 0 &&
                           sortedRows.length > 0 && typeof sortedRows[0][col] === 'number')
                        )
                        const value = calculateTotals[col]
                        // Only show "Total" in the first dimension column
                        const displayValue = isDimensionColumn
                          ? (colIndex === 0 ? 'Total' : '')
                          : (typeof value === 'number' ? formatNumber(value) : (value || ''))

                        // Sticky style for dimension columns in totals row when frozen
                        const stickyStyle = isDimensionColumn && freezeDimensions ? {
                          position: 'sticky' as const,
                          left: dimensionIndex * 120,
                          zIndex: 15 - dimensionIndex,
                        } : {}

                        // Current aggregation method for this column
                        const matchingMetric = appliedMetrics.find(metric => col === metric || col.endsWith(` | ${metric}`))
                        const currentAgg = columnAggregations[col] || (matchingMetric ? columnAggregations[matchingMetric] : undefined) || 'sum'

                        return (
                          <TableCell
                            key={col}
                            className={`font-semibold bg-slate-50 ${isDimensionColumn ? 'text-slate-700' : isMetricColumn ? 'text-slate-900' : 'text-slate-500'}`}
                            style={{
                              textAlign: isMetricColumn ? 'right' : 'left',
                              direction: 'ltr',
                              minWidth: isDimensionColumn && freezeDimensions ? 120 : undefined,
                              ...stickyStyle
                            }}
                          >
                            {isMetricColumn ? (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    className="w-full text-right cursor-pointer hover:text-blue-600 transition-colors focus:outline-none"
                                    title={`Total: ${currentAgg.toUpperCase()} (click to change)`}
                                  >
                                    {displayValue}
                                    {currentAgg !== 'sum' && (
                                      <span className="ml-1 text-[10px] text-slate-400 uppercase">{currentAgg}</span>
                                    )}
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-32">
                                  <DropdownMenuLabel className="text-xs text-slate-500">Total aggregation</DropdownMenuLabel>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuRadioGroup
                                    value={currentAgg}
                                    onValueChange={(newAgg) => {
                                      const key = matchingMetric || col
                                      setColumnAggregations(prev => {
                                        const next = { ...prev }
                                        if (newAgg === 'sum') {
                                          delete next[key]
                                        } else {
                                          next[key] = newAgg
                                        }
                                        return next
                                      })
                                      setHasUnsavedChanges(true)
                                    }}
                                  >
                                    <DropdownMenuRadioItem value="sum">Sum</DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem value="avg">Avg</DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem value="min">Min</DropdownMenuRadioItem>
                                    <DropdownMenuRadioItem value="max">Max</DropdownMenuRadioItem>
                                  </DropdownMenuRadioGroup>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ) : (
                              displayValue
                            )}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  )}
                </TableBody>
              </Table>
                  </div>
                </ContextMenuTrigger>
                <ContextMenuContent className="w-48">
                  <ContextMenuLabel>Table Settings</ContextMenuLabel>
                  <div className="px-2 py-1.5 flex items-center justify-between">
                    <span className="text-sm">Short Numbers</span>
                    <Switch checked={showNumFormatted} onCheckedChange={setShowNumFormatted} className="scale-75" />
                  </div>
                  <div className="px-2 py-1.5 flex items-center justify-between">
                    <span className="text-sm">Show Totals</span>
                    <Switch checked={showTotals} onCheckedChange={setShowTotals} className="scale-75" />
                  </div>
                  <div className="px-2 py-1.5 flex items-center justify-between">
                    <span className="text-sm">Hide Empty Columns</span>
                    <Switch checked={hideEmptyColumns} onCheckedChange={setHideEmptyColumns} className="scale-75" />
                  </div>
                  <div className="px-2 py-1.5 flex items-center justify-between">
                    <span className="text-sm">Freeze Dimensions</span>
                    <Switch checked={freezeDimensions} onCheckedChange={setFreezeDimensions} className="scale-75" />
                  </div>
                </ContextMenuContent>
              </ContextMenu>
            </div>
          )}
      </Card>
      )}

      {/* Maximized View - Rendered as Portal */}
      {isMaximized && createPortal(
        <div className="fixed inset-0 z-[9999] flex flex-col bg-background">
          {/* Overlay background */}
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsMaximized(false)} />
          
          {/* Maximized Card */}
          <Card className="relative z-10 m-0 rounded-none h-full flex flex-col">
            <CardContent className="flex-1 overflow-hidden p-0">
              {data.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  {hasRunReport ? (
                    <>
                      <p className="text-lg text-muted-foreground mb-2">
                        No data found!
                      </p>
                      <p className="text-sm text-muted-foreground max-w-md mx-auto">
                        query: {appliedMetrics.length > 0 ? appliedMetrics.join(' ') : ''}
                        {appliedDimensions.length > 0 ? ` ${appliedDimensions.join(' ')}` : ''}
                        {appliedPivot.length > 0 ? ` pivot ${appliedPivot.join(' ')}` : ''}
                        {dateRange?.from || dateRange?.to ? ` ${dateRange?.from ? formatDateForAPI(dateRange.from) : ''}..${dateRange?.to ? formatDateForAPI(dateRange.to) : ''}` : ''}
                        {conditionals.trim() ? ` if ${conditionals.trim()}` : ''}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-lg text-muted-foreground mb-2">
                        No data to display
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Select metrics and dimensions above, then click Run to generate
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <div className="h-full p-4">
                  <div className="border rounded-md h-full flex flex-col">
                    {/* Settings bar above table */}
                    <div className="flex items-center justify-between gap-1 px-2 py-1 border-b bg-muted/30 rounded-t-md">
                      {/* Report name on left */}
                      <span className="text-sm font-bold text-slate-700">
                        {selectedReport?.name || reportName || getReportTitle()}
                      </span>
                      {/* Settings icons on right */}
                      <div className="flex items-center gap-1">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-1 rounded hover:bg-muted">
                            <Settings className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="z-[10001] w-48">
                          <DropdownMenuLabel>Table Settings</DropdownMenuLabel>
                          <div className="px-2 py-1.5 flex items-center justify-between">
                            <span className="text-sm">Short Numbers</span>
                            <Switch checked={showNumFormatted} onCheckedChange={setShowNumFormatted} className="scale-75" />
                          </div>
                          <div className="px-2 py-1.5 flex items-center justify-between">
                            <span className="text-sm">Show Totals</span>
                            <Switch checked={showTotals} onCheckedChange={setShowTotals} className="scale-75" />
                          </div>
                          <div className="px-2 py-1.5 flex items-center justify-between">
                            <span className="text-sm">Hide Empty Columns</span>
                            <Switch checked={hideEmptyColumns} onCheckedChange={setHideEmptyColumns} className="scale-75" />
                          </div>
                          <div className="px-2 py-1.5 flex items-center justify-between">
                            <span className="text-sm">Freeze Dimensions</span>
                            <Switch checked={freezeDimensions} onCheckedChange={setFreezeDimensions} className="scale-75" />
                          </div>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <button className="p-1 rounded hover:bg-muted disabled:opacity-50" onClick={exportData} disabled={data.length === 0} title="Download Report">
                        <Download className="h-4 w-4 text-muted-foreground" />
                      </button>
                      <button className="p-1 rounded hover:bg-muted" onClick={handleShareReport} title="Share Report">
                        <Share2 className="h-4 w-4 text-muted-foreground" />
                      </button>
                      <button className="p-1 rounded hover:bg-muted" onClick={() => setIsMaximized(false)} title="Exit Fullscreen">
                        <Minimize2 className="h-4 w-4 text-muted-foreground" />
                      </button>
                      </div>
                    </div>
                    {/* Desktop: Right-click context menu */}
                    <ContextMenu>
                      <ContextMenuTrigger asChild>
                        <div className="flex-1 overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {visibleColumns.map((col, colIndex) => {
                            const isPivotColumn = createPivotTable.pivotColumns?.includes(col) || false
                            const isDimensionColumn = appliedDimensions.includes(col)
                            const dimensionIndex = isDimensionColumn ? appliedDimensions.indexOf(col) : -1
                            // Dimension columns should never be treated as metrics, even if they contain numbers
                            const isMetricColumn = !isDimensionColumn && (
                              appliedMetrics.includes(col) ||
                              appliedMetrics.some(metric => col.includes(metric)) ||
                              isPivotColumn ||
                              // For non-pivot reports, check if column is a metric by checking if it's numeric
                              (createPivotTable.pivotColumns?.length === 0 && appliedMetrics.length > 0 &&
                               sortedRows.length > 0 && typeof sortedRows[0][col] === 'number')
                            )

                            let backgroundClass = 'bg-zinc-50'
                            let textClass = 'text-zinc-700'
                            let borderClass = ''

                            if (isPivotColumn) {
                              backgroundClass = 'bg-zinc-300'
                              textClass = 'text-zinc-900'
                              borderClass = 'border-l border-zinc-500'
                            } else if (isDimensionColumn) {
                              backgroundClass = 'bg-zinc-100'
                              textClass = 'text-zinc-800'
                              borderClass = dimensionIndex === 0 ? '' : 'border-l border-zinc-300'
                            } else if (isMetricColumn) {
                              backgroundClass = 'bg-zinc-200'
                              textClass = 'text-zinc-800'
                              borderClass = 'border-l border-zinc-400'
                            }

                            // All header cells are sticky top, dimension columns also sticky left when frozen
                            const stickyStyle = {
                              position: 'sticky' as const,
                              top: 0,
                              ...(isDimensionColumn && freezeDimensions ? {
                                left: dimensionIndex * 120,
                                zIndex: 40 - dimensionIndex, // Higher z-index for dimension columns in header
                              } : {
                                zIndex: 20, // Lower z-index for non-dimension header columns
                              })
                            }

                            const isSorted = sortConfig.column === col

                            return (
                              <TableHead
                                key={col}
                                className={`font-medium ${backgroundClass} ${textClass} ${borderClass} cursor-pointer hover:bg-zinc-200 select-none`}
                                style={{
                                  textAlign: isMetricColumn ? 'right' : 'left',
                                  direction: 'ltr',
                                  minWidth: isDimensionColumn && freezeDimensions ? 120 : undefined,
                                  ...stickyStyle
                                }}
                                onClick={() => handleSortClick(col)}
                              >
                                <div className={`flex items-center gap-1 ${isMetricColumn ? 'justify-end' : ''}`}>
                                  <span>{col}</span>
                                  {isSorted && (
                                    sortConfig.direction === 'asc'
                                      ? <ArrowUp className="h-4 w-4" />
                                      : <ArrowDown className="h-4 w-4" />
                                  )}
                                </div>
                              </TableHead>
                            )
                          })}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedRows.map((row, index) => (
                          <TableRow key={index}>
                            {visibleColumns.map((col, colIndex) => {
                              const isPivotColumn = createPivotTable.pivotColumns?.includes(col) || false
                              const isDimensionColumn = appliedDimensions.includes(col)
                              const dimensionIndex = isDimensionColumn ? appliedDimensions.indexOf(col) : -1
                              // Dimension columns should never be treated as metrics, even if they contain numbers
                              const isMetricColumn = !isDimensionColumn && (
                                appliedMetrics.includes(col) ||
                                appliedMetrics.some(metric => col.includes(metric)) ||
                                isPivotColumn ||
                                // For non-pivot reports, check if column is a metric by checking if it's numeric (string or number)
                                (createPivotTable.pivotColumns?.length === 0 && appliedMetrics.length > 0 &&
                                 sortedRows.length > 0 &&
                                 (typeof sortedRows[0][col] === 'number' ||
                                  (typeof sortedRows[0][col] === 'string' && !isNaN(parseFloat(sortedRows[0][col])))))
                              )

                              const value = row[col]

                              let cellClass = ''

                              if (isPivotColumn) {
                                cellClass = 'border-l border-zinc-500 bg-zinc-300/20'
                              } else if (isDimensionColumn) {
                                cellClass = dimensionIndex === 0 ? 'bg-zinc-100' : 'border-l border-zinc-300 bg-zinc-100'
                              } else if (isMetricColumn) {
                                cellClass = 'border-l border-zinc-400 bg-white'
                              }

                              // Sticky style for dimension columns when frozen
                              const stickyStyle = isDimensionColumn && freezeDimensions ? {
                                position: 'sticky' as const,
                                left: dimensionIndex * 120,
                                zIndex: 10 - dimensionIndex,
                              } : {}

                              return (
                                <TableCell
                                  key={col}
                                  className={cellClass}
                                  style={{
                                    textAlign: isMetricColumn ? 'right' : 'left',
                                    direction: 'ltr',
                                    minWidth: isDimensionColumn && freezeDimensions ? 120 : undefined,
                                    ...stickyStyle
                                  }}
                                >
                                  {isMetricColumn && (typeof value === 'number' || (typeof value === 'string' && value !== '' && !isNaN(Number(value))))
                                    ? formatNumber(Number(value))
                                    : (value || '-')}
                                </TableCell>
                              )
                            })}
                          </TableRow>
                        ))}
                        {calculateTotals && (
                          <TableRow className="border-t-2 border-slate-200 bg-slate-50 font-semibold sticky bottom-0 z-10">
                            {visibleColumns.map((col, colIndex) => {
                              const isDimensionColumn = appliedDimensions.includes(col)
                              const dimensionIndex = isDimensionColumn ? appliedDimensions.indexOf(col) : -1
                              // Dimension columns should never be treated as metrics, even if they contain numbers
                              const isMetricColumn = !isDimensionColumn && (
                                appliedMetrics.includes(col) ||
                                appliedMetrics.some(metric => col.includes(metric)) ||
                                createPivotTable.pivotColumns?.includes(col) ||
                                // For non-pivot reports, check if column is a metric by checking if it's numeric (string or number)
                                (createPivotTable.pivotColumns?.length === 0 && appliedMetrics.length > 0 &&
                                 sortedRows.length > 0 &&
                                 (typeof sortedRows[0][col] === 'number' ||
                                  (typeof sortedRows[0][col] === 'string' && !isNaN(parseFloat(sortedRows[0][col])))))
                              )
                              const value = calculateTotals[col]
                              // Only show "Total" in the first dimension column
                              const displayValue = isDimensionColumn
                                ? (colIndex === 0 ? 'Total' : '')
                                : (typeof value === 'number' ? formatNumber(value) : (value || ''))

                              // Sticky style for dimension columns in totals row when frozen
                              const stickyStyle = isDimensionColumn && freezeDimensions ? {
                                position: 'sticky' as const,
                                left: dimensionIndex * 120,
                                zIndex: 15 - dimensionIndex,
                              } : {}

                              return (
                                <TableCell
                                  key={col}
                                  className={`font-semibold bg-slate-50 ${isDimensionColumn ? 'text-slate-700' : isMetricColumn ? 'text-slate-900' : 'text-slate-500'}`}
                                  style={{
                                    textAlign: isMetricColumn ? 'right' : 'left',
                                    direction: 'ltr',
                                    minWidth: isDimensionColumn && freezeDimensions ? 120 : undefined,
                                    ...stickyStyle
                                  }}
                                >
                                  {displayValue}
                                </TableCell>
                              )
                            })}
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                        </div>
                      </ContextMenuTrigger>
                      <ContextMenuContent className="z-[10001] w-48">
                        <ContextMenuLabel>Table Settings</ContextMenuLabel>
                        <div className="px-2 py-1.5 flex items-center justify-between">
                          <span className="text-sm">Short Numbers</span>
                          <Switch checked={showNumFormatted} onCheckedChange={setShowNumFormatted} className="scale-75" />
                        </div>
                        <div className="px-2 py-1.5 flex items-center justify-between">
                          <span className="text-sm">Show Totals</span>
                          <Switch checked={showTotals} onCheckedChange={setShowTotals} className="scale-75" />
                        </div>
                        <div className="px-2 py-1.5 flex items-center justify-between">
                          <span className="text-sm">Hide Empty Columns</span>
                          <Switch checked={hideEmptyColumns} onCheckedChange={setHideEmptyColumns} className="scale-75" />
                        </div>
                        <div className="px-2 py-1.5 flex items-center justify-between">
                          <span className="text-sm">Freeze Dimensions</span>
                          <Switch checked={freezeDimensions} onCheckedChange={setFreezeDimensions} className="scale-75" />
                        </div>
                      </ContextMenuContent>
                    </ContextMenu>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Report</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedReport?.name}"? This action cannot be undone.
              {(() => {
                const usedBy = agentsUsingReport(selectedReport?.id)
                if (usedBy.length === 0) return null
                return (
                  <span className="mt-3 block rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                    This report is used as the dataset by <strong>{usedBy.length}</strong>{' '}
                    agent{usedBy.length === 1 ? '' : 's'} ({usedBy.map(a => a.name).join(', ')}).
                    They will lose this dataset but keep running.
                  </span>
                )
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteReport}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}